import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import MainCard from "../../../../../components/MainCard";

import { useTheme } from "@mui/material/styles";
import {
  SnackbarProps,
  Stack,
  Tooltip,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import _debounce from "lodash/debounce";
// third-party
import { ColumnDef } from "@tanstack/react-table";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import Loader from "../../../../../components/Loader";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableSimpleTable from "../../../../../components/ReusableComponent/ReusableSimpleTable";
import { openSnackbar } from "../../../../../api/snackbar";
import ReusableDeleteDialog from "../../../../../components/ReusableComponent/ReusableDeleteDialog";
import { EyeOutlined, CloseOutlined } from "@ant-design/icons";
import ExpandingMiniInterviewAnswerDetail from "./ExpandingMiniInterviewAnswerDetail";

interface ResumeMiniInterviewAnswer {
  id: number;
  resume_id: number;
  question: string;
  answer: string;
  status: boolean;
  read_by_admin: boolean;
  created_at: string;
  updated_at: string;
}

export const ResumeMiniInterviewAnswer = ({
  resume_id,
}: {
  resume_id?: number;
}) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);

  const [expandedRowId, setExpandedRowId] = useState<string | number | null>(
    null
  );
  const [selected, setSelected] = useState<ResumeMiniInterviewAnswer | null>(
    null
  );

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResumeMiniInterviewAnswer[]>([]);

  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    setOpen(!open);
  };

  const columns = useMemo<ColumnDef<ResumeMiniInterviewAnswer>[]>(
    () => [
      // {
      //   header: () => <FormattedMessage id="ID" />,
      //   accessorKey: "id",
      //   meta: { className: "cell-center" },
      //   cell: ({ row }) => (
      //     <Stack direction="row" spacing={1.5} alignItems="center">
      //       <Stack spacing={0}>
      //         <Typography variant="subtitle2">{row?.original.id}</Typography>
      //       </Stack>
      //     </Stack>
      //   ),
      // },
      {
        header: () => <FormattedMessage id="Question" />,
        accessorKey: "question",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.question?.length > 50
                  ? `${row?.original.question.substring(0, 50)}...`
                  : row?.original.question}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Answer" />,
        accessorKey: "answer",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.answer?.length > 50
                  ? `${row?.original.answer.substring(0, 50)}...`
                  : row?.original.answer}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Status" />,
        accessorKey: "status",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography
                variant="subtitle2"
                color={row?.original.status ? "success.main" : "error.main"}
              >
                {row?.original.status ? "Active" : "Inactive"}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Read by Admin" />,
        accessorKey: "read_by_admin",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography
                variant="subtitle2"
                color={
                  row?.original.read_by_admin ? "success.main" : "warning.main"
                }
              >
                {row?.original.read_by_admin ? "Read" : "Unread"}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => {
          const isExpanded = String(expandedRowId) === String(row.original.id);
          const collapseIcon = isExpanded ? (
            <CloseOutlined style={{ color: theme.palette.error.main }} />
          ) : (
            <EyeOutlined />
          );
          return (
            <Stack direction="row">
              <Tooltip title={isExpanded ? "close details" : "view details"}>
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedRowId(
                      isExpanded ? null : String(row.original.id)
                    );
                  }}
                  sx={{ "&:hover": { backgroundColor: "transparent" } }}
                >
                  {collapseIcon}
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                    setSelected(row.original);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [theme, expandedRowId]
  );

  const handleDelete = async () => {
    if (!selected?.id) return;
    setIsDeleting(true);
    try {
      await USERAXIOS.delete(`/resumeMiniInterviewAnswer/${selected?.id}`);
      setResult((prevResults: any[]) =>
        prevResults.filter((item) => item.id !== selected?.id)
      );
      openSnackbar({
        open: true,
        message: "Mini Interview Answer deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        close: false,
        alert: {
          color: "success",
        },
      } as SnackbarProps);
      if (handleClose) handleClose();
    } catch (error: any) {
      openSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to delete mini interview answer",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await USERAXIOS.get(`/resumeMiniInterviewAnswer`);
      const { data, meta } = response.data;
      setResult(data);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching mini interview answers",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <MainCard title="Resume Mini Interview Answers">
          <ReusableSimpleTable
            data={result}
            columns={columns}
            renderDetailPanel={({ row }: any) => (
              <ExpandingMiniInterviewAnswerDetail data={row.original} />
            )}
            expandedRowId={expandedRowId}
            setExpandedRowId={setExpandedRowId}
          />
        </MainCard>
      )}
      <ReusableDeleteDialog
        title={selected?.question}
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default ResumeMiniInterviewAnswer;
